import type { MyClassesView, PlanView } from '@classgo/shared';

import type { AlbertPair } from '../../albert/types';
import type { TopicOption } from './types';

/**
 * The teacher's pairs that Albert can plan for: every class with a subject (a homeroom-only
 * assignment has no subject and nothing to plan), one entry per group × subject.
 *
 * @param {MyClassesView | undefined} classes -- `GET /me/classes`.
 * @returns {AlbertPair[]} The pairs, as listed.
 */
export const pairsOf = (classes: MyClassesView | undefined): AlbertPair[] =>
  (classes?.items ?? []).flatMap((item) =>
    item.subjectId && item.subjectName
      ? [
          {
            groupId: item.groupId,
            groupName: item.groupName,
            gradeId: item.gradeId,
            gradeName: item.gradeName,
            subjectId: item.subjectId,
            subjectName: item.subjectName,
          },
        ]
      : [],
  );

/**
 * The key a pair is selected by.
 */
export const pairKey = (pair: { groupId: string; subjectId: string }) =>
  `${pair.groupId}:${pair.subjectId}`;

/**
 * The topics of a plan in the order the plan lays them out, each with its period's name.
 *
 * @param {PlanView | undefined} plan -- `GET /curriculum/plan`.
 * @returns {TopicOption[]} The topics.
 */
export const topicsOf = (plan: PlanView | undefined): TopicOption[] =>
  (plan?.periods ?? []).flatMap((period) =>
    period.topics.map((topic) => ({
      ...topic,
      periodName:
        plan?.yearPeriods.find((entry) => entry.position === period.periodPosition)?.name ??
        `Periodo ${period.periodPosition}`,
    })),
  );

/**
 * The bullet points of a draft from the textarea: one per line, trimmed, without a leading
 * dash or bullet, empty lines dropped, at most twelve (the API's limit).
 *
 * @param {string} text -- What the teacher typed.
 * @returns {string[]} The bullets.
 */
export const bulletsOf = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
    .filter((line) => line.length > 0)
    .slice(0, 12);

/**
 * A date-time as the screen prints it ("28 sept 2026, 10:15").
 *
 * @param {string} iso -- An ISO timestamp.
 * @returns {string} The formatted value.
 */
export const formatDateTime = (iso: string): string =>
  new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
